import { createAsyncThunk, createDraftSafeSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SpecialMarket, SpecialMarketCriteria, SpecialMarketSettings } from "api/models/specialMarkets";
import { specialMarketService } from "api/services";
import { RootState } from "app";

export type SpecialMarketState = {
    specialMarkets: SpecialMarket[];
    settings: SpecialMarketSettings;
};

const initialState: SpecialMarketState = {
    specialMarkets: [],
    settings: {
        code: "",
        branding: []
    }
};

export const getSpecialMarkets = createAsyncThunk(
    "specialMarkets/get",
    async(criteria: SpecialMarketCriteria) => await specialMarketService.get(criteria)
);

export const getSpecialMarketSettings = createAsyncThunk(
    "specialMarkets/settings/get",
    async() => await specialMarketService.getSettings()
);

export const saveSpecialMarket = createAsyncThunk(
    "companyBrandSpecialMarket/save",
    async(request: {brandId: number, selected: boolean}, { rejectWithValue, getState }) => {
        const { specialMarkets: {settings, specialMarkets} } = getState() as RootState;

        const specialMarket = specialMarkets.find(cbv => cbv.brandId === request.brandId);
        if (!specialMarket) {
            return rejectWithValue("Special market not found for the given brandId");
        }

        let specialMarketIds = specialMarket.specialMarketIds || [];

        if(request.selected && !specialMarketIds.includes(settings.code)) {
            specialMarketIds = [...specialMarketIds, settings.code];          
        }
        if (!request.selected) {
            specialMarketIds = specialMarketIds.filter(id => id !== settings.code);
        }

        const update = {...specialMarket, specialMarketIds: specialMarketIds}

        await specialMarketService.save(update);
        return update;
    }
);

const specialMarketsSlice = createSlice({
    name: "specialMarkets",
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder.addCase(
            getSpecialMarkets.fulfilled,
            (state, action:PayloadAction<SpecialMarket[] | null>) =>{
                if (action.payload)
                    state.specialMarkets = action.payload;
                else
                    state.specialMarkets = [];
            }
        );
        builder.addCase(
            saveSpecialMarket.fulfilled,
            (state, action:PayloadAction<SpecialMarket>) => {
                const updated = action.payload;

                const index = state.specialMarkets.findIndex(cbv => cbv.brandId === updated.brandId);
                if (index !== -1) {
                    state.specialMarkets[index].specialMarketIds = updated.specialMarketIds;
                }
            }
        );
        builder.addCase(
            getSpecialMarketSettings.fulfilled,
            (state, action: PayloadAction<SpecialMarketSettings | null>) =>{
                if (action.payload) {
                    state.settings = action.payload;
                }
            }
        );
    },
});

const selectSelf = (state: RootState) => state.specialMarkets;
export const selectSpecialMarkets = createDraftSafeSelector(
    selectSelf,
    (state) => state.specialMarkets.map(x => ({
        brandId: x.brandId,
        name: state.settings.branding.find(b => b.brandId === x.brandId)?.name || "",
        selected: x.specialMarketIds?.some(id => id === state.settings.code)
    }))
)

export const selectSpecialMarketSettings = createDraftSafeSelector(
    selectSelf,
    (state) => state.settings
)

export default specialMarketsSlice.reducer;